import api, { handleResponse, handleError } from './api';

/**
 * Paid club memberships — quote, checkout, plan switching, cancel, owner actions.
 * Mirrors the backend ClubMembershipPaymentService surface 1:1.
 */
const clubMembershipService = {
  async getQuote(clubId) {
    try { return handleResponse(await api.get(`/clubs/${clubId}/membership/quote`)); }
    catch (e) { return handleError(e); }
  },

  async checkout(clubId, { stripePaymentMethodId = null, skipBalance = false } = {}) {
    try {
      return handleResponse(await api.post(`/clubs/${clubId}/membership/checkout`, {
        stripePaymentMethodId, skipBalance,
      }));
    } catch (e) { return handleError(e); }
  },

  async cancel(clubId, atPeriodEnd = true) {
    try { return handleResponse(await api.post(`/clubs/${clubId}/membership/cancel?atPeriodEnd=${atPeriodEnd}`)); }
    catch (e) { return handleError(e); }
  },

  async switchPlan(clubId, { target, stripePaymentMethodId = null, skipBalance = false }) {
    try {
      return handleResponse(await api.post(`/clubs/${clubId}/membership/switch`, {
        target, stripePaymentMethodId, skipBalance,
      }));
    } catch (e) { return handleError(e); }
  },

  async refundMember(clubId, memberUserId) {
    try { return handleResponse(await api.post(`/clubs/${clubId}/membership/${memberUserId}/refund`)); }
    catch (e) { return handleError(e); }
  },

  async compMembership(clubId, userId, expiresAt = null) {
    try { return handleResponse(await api.post(`/clubs/${clubId}/membership/comp`, { userId, expiresAt })); }
    catch (e) { return handleError(e); }
  },

  // Owner dashboard
  async getOwnerStats(clubId) {
    try { return handleResponse(await api.get(`/clubs/${clubId}/owner/stats`)); }
    catch (e) { return handleError(e); }
  },

  async listMembersForOwner(clubId, { status = null } = {}) {
    try {
      const params = status ? `?status=${encodeURIComponent(status)}` : '';
      return handleResponse(await api.get(`/clubs/${clubId}/owner/members${params}`));
    } catch (e) { return handleError(e); }
  },

  async listInvoicesForOwner(clubId, { page = 1, pageSize = 50 } = {}) {
    try { return handleResponse(await api.get(`/clubs/${clubId}/owner/invoices?page=${page}&pageSize=${pageSize}`)); }
    catch (e) { return handleError(e); }
  },

  async updateMembershipConfig(clubId, { mode, priceCents, billingPeriod, termDays, currency }) {
    try {
      return handleResponse(await api.put(`/clubs/${clubId}/membership/config`, {
        mode, priceCents, billingPeriod, termDays, currency,
      }));
    } catch (e) { return handleError(e); }
  },

  // ---- Events + clubs redesign (2026-09), agent E1 — additive ----
  /**
   * Paid invoices grouped by month for the MEMBERSHIPS & REVENUE rail
   * (PLAN.md backend contract §12). `{ currency, months:[{ month, label, amountCents,
   * invoiceCount, prServiceFeeCents, gstCents, stripeFeeCents }], thisMonth:{ memberships,
   * amountCents, prServiceFeeCents, gstCents, stripeFeeCents, yoursCents } }`.
   * Owner/admin only. Callers fall back to the flat stats when this 404s.
   */
  async getRevenueHistory(clubId, months = 6) {
    try { return handleResponse(await api.get(`/clubs/${clubId}/owner/revenue-history?months=${months}`)); }
    catch (e) { return handleError(e); }
  },
};

export default clubMembershipService;
